import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Truck, Search, Filter, Loader2, Download, ScanLine, RotateCcw, Paperclip } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { SignedFileLink } from '@/components/ui/SignedImage';
import { DateRangePicker, rangeForPreset, DatePreset } from '@/components/ui/DateRangePicker';
import { ScanDialog } from './ScanDialog';
import { toCommaList } from '@/utils/search-list';

/** The appointment booked against a scanned LR, as the tracking list now returns it. */
interface TrackingAppointment {
  id: number;
  appointmentRef?: string | null;
  appointmentDate?: string | null;
  appointmentTime?: string | null;
  poNumber?: string | null;
  pending?: boolean;
}

interface TrackingRow {
  appointment?: TrackingAppointment | null;
  id: string;
  awbNo?: string;
  lrNo?: string;
  statusId: number;
  statusMessage?: string;
  eventDateTime: string;
  createdAt: string;
  scanCity?: string;
  scanState?: string;
  scanPincode?: string;
  scanLat?: string;
  scanLng?: string;
  chargedWeight?: string;
  lengthCm?: string;
  widthCm?: string;
  heightCm?: string;
  byUserId?: number;
  byUserName?: string | null;
  assignedUserId?: number;
  assignedUserName?: string | null;
  podFile?: string;
  receiverName?: string;
  receiverPhone?: string;
  rawPayload?: any;
  courier?: { id: number; name: string; code: string };
  order?: {
    id: string; orderNo: string; awbNo?: string; lrNo?: string;
    customerName: string; customerMobile: string;
    merchant: { id: number; companyName: string };
  };
}

interface StatusRow { id: number; name: string }
interface UserOpt { id: number; name?: string; email: string; mobile?: string }

// Whole-row highlight so Delivered logs read green and Delivery-Failed / RTO-lost logs read red at a glance.
const ROW_TINT: Record<number, string> = {
  8: 'bg-emerald-50/70', 19: 'bg-emerald-50/70', 12: 'bg-emerald-50/70',
  9: 'bg-rose-50/70', 13: 'bg-rose-50/70', 14: 'bg-rose-50/70',
};

const STATUS_TONE: Record<number, string> = {
  1: 'bg-blue-100 text-blue-700', 2: 'bg-blue-100 text-blue-700',
  3: 'bg-amber-100 text-amber-700', 4: 'bg-amber-100 text-amber-700', 5: 'bg-amber-100 text-amber-700',
  6: 'bg-indigo-100 text-indigo-700', 7: 'bg-indigo-100 text-indigo-700',
  8: 'bg-emerald-100 text-emerald-700', 19: 'bg-emerald-100 text-emerald-700',
  9: 'bg-rose-100 text-rose-700', 13: 'bg-rose-100 text-rose-700', 14: 'bg-rose-100 text-rose-700',
  10: 'bg-violet-100 text-violet-700', 11: 'bg-violet-100 text-violet-700', 12: 'bg-violet-100 text-violet-700',
  17: 'bg-amber-100 text-amber-800', 18: 'bg-amber-100 text-amber-700',
  20: 'bg-slate-100 text-slate-500', 21: 'bg-rose-50 text-rose-600',
  23: 'bg-slate-200 text-slate-600', 24: 'bg-slate-100 text-slate-500',
};

export function TrackingPage({ orderType }: { orderType: 'B2C' | 'B2B' }) {
  const [awbNo, setAwbNo] = useState('');
  const [statusId, setStatusId] = useState('');
  const [fromUserId, setFromUserId] = useState('');
  const [toUserId, setToUserId] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const initial = rangeForPreset('today');
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);
  const [preset, setPreset] = useState<DatePreset>('today');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [showScan, setShowScan] = useState(false);
  // Same splitter the backend uses — shown back to the operator so a paste that silently lost a line
  // (or picked up a stray blank) is visible before the search runs, not after.
  const searchTermCount = awbNo.split(/[\s,;|]+/).filter(Boolean).length;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['tracking', { orderType, awbNo, statusId, fromUserId, toUserId, startDate, endDate, page }],
    queryFn: async () => {
      const params: any = { orderType, page, pageSize, startDate, endDate };
      if (awbNo) params.awbNo = awbNo;
      if (statusId) params.statusId = statusId;
      if (fromUserId) params.fromUserId = fromUserId;
      if (toUserId) params.toUserId = toUserId;
      return (await api.get<{ total: number; items: TrackingRow[] }>('/tracking', { params })).data;
    },
  });

  const { data: ndrStatuses } = useQuery({
    queryKey: ['ndr-statuses-track'],
    queryFn: async () => {
      try { const r: any = (await api.get('/settings/ndr_statuses')).data; return (Array.isArray(r) ? r : (r?.items ?? [])) as Array<{ id: number; name: string }>; } catch { return []; }
    },
  });

  const { data: statuses } = useQuery({
    queryKey: ['order-statuses'],
    queryFn: async () => {
      try {
        const r: any = (await api.get('/settings/order_statuses')).data;
        return Array.isArray(r) ? (r as StatusRow[]) : ((r?.items ?? []) as StatusRow[]);
      } catch { return [] as StatusRow[]; }
    },
  });
  const { data: users } = useQuery({
    queryKey: ['users-for-filter'],
    queryFn: async () => {
      try {
        const r: any = (await api.get('/users', { params: { isActive: 'true' } })).data;
        return Array.isArray(r) ? (r as UserOpt[]) : ((r?.items ?? []) as UserOpt[]);
      } catch { return [] as UserOpt[]; }
    },
  });

  const statusMap = useMemo(() => {
    const m: Record<number, string> = {};
    (statuses ?? []).forEach(s => { m[s.id] = s.name; });
    return m;
  }, [statuses]);

  const items = data?.items ?? [];
  const userLabel = (uid?: number, preResolved?: string | null): string => {
    if (preResolved) return preResolved;
    if (!uid) return 'N/A';
    const u = (users ?? []).find(x => x.id === uid);
    if (u) {
      const nm = (u as any).firstName ? `${(u as any).firstName ?? ''} ${(u as any).lastName ?? ''}`.trim() : u.name;
      return nm ? `${nm} (${(u as any).empCode ?? uid})` : `User #${uid}`;
    }
    return `User #${uid}`;
  };

  const total = data?.total ?? 0;

  function reset() {
    setAwbNo(''); setStatusId(''); setFromUserId(''); setToUserId('');
    const t = rangeForPreset('today');
    setStartDate(t.startDate); setEndDate(t.endDate); setPreset('today');
    setPage(1);
  }

  function exportCsv() {
    const qs = new URLSearchParams();
    qs.set('orderType', orderType);
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    if (awbNo) qs.set('awbNo', awbNo);
    downloadBlob(`/tracking/export?${qs.toString()}`, `tracking-${orderType}-${today}.csv`);
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tracking — {orderType}</h1>
          <p className="text-sm text-slate-500">Manual scans and tracking history for {orderType} shipments</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setShowScan(true)} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90">
            <ScanLine size={14} /> Scan
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 grid grid-cols-6 gap-3">
        <div className="col-span-1">
          <label className="text-xs font-medium text-slate-500 mb-1 block">
            {orderType === 'B2B' ? 'LR / AWB No.' : 'AWB / LR No.'}
            {searchTermCount > 1 && <span className="ml-1 font-normal text-primary">({searchTermCount} numbers)</span>}
          </label>
          {/* A textarea, not an input: operators paste a column straight out of Excel or a manifest,
              which arrives as newline-separated text. Commas, spaces and semicolons work too, and
              Enter still runs the search so single-value typing is unchanged. */}
          <textarea
            value={awbNo}
            // Spaces and line breaks between numbers become commas as you type or paste.
            onChange={(e) => setAwbNo(toCommaList(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); setPage(1); refetch(); } }}
            rows={1}
            className="input w-full resize-y min-h-[38px] font-mono text-xs"
            placeholder={orderType === 'B2B' ? 'LR or AWB — paste many, spaces become commas' : 'AWB or LR — paste many, spaces become commas'}
          />
        </div>
        <div className="col-span-1">
          <label className="text-xs font-medium text-slate-500 mb-1 block">From User</label>
          <select value={fromUserId} onChange={(e) => setFromUserId(e.target.value)} className="input w-full">
            <option value="">All users</option>
            {(users ?? []).map(u => (<option key={u.id} value={u.id}>{u.name ? `${u.name} (${u.id})` : `User #${u.id}`}</option>))}
          </select>
        </div>
        <div className="col-span-1">
          <label className="text-xs font-medium text-slate-500 mb-1 block">To User</label>
          <select value={toUserId} onChange={(e) => setToUserId(e.target.value)} className="input w-full">
            <option value="">All users</option>
            {(users ?? []).map(u => (<option key={u.id} value={u.id}>{u.name ? `${u.name} (${u.id})` : `User #${u.id}`}</option>))}
          </select>
        </div>
        <div className="col-span-1">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Tracking Event</label>
          <select value={statusId} onChange={(e) => setStatusId(e.target.value)} className="input w-full">
            <option value="">All events</option>
            {(statuses ?? []).map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Date Range</label>
          <DateRangePicker value={{ startDate, endDate }} defaultPreset={preset}
            onChange={(r) => { setStartDate(r.startDate); setEndDate(r.endDate); setPreset(r.preset); setPage(1); }} />
        </div>
        <div className="col-span-6 flex items-center gap-2 pt-1">
          <button onClick={() => { setPage(1); refetch(); }} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
            <Search size={14} /> Search
          </button>
          <button onClick={reset} className="px-4 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5">
            <RotateCcw size={14} /> Reset
          </button>
          <div className="ml-auto text-xs text-slate-500"><Filter size={12} className="inline mr-1" /> {total.toLocaleString('en-IN')} results</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Order Details</th>
                <th className="text-left px-4 py-3">Tracking Details</th>
                <th className="text-left px-4 py-3">User Details</th>
                <th className="text-left px-4 py-3">Location Details</th>
                <th className="text-left px-4 py-3">Assigned User</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={5} className="text-center py-12 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>)}
              {!isLoading && items.length === 0 && (<tr><td colSpan={5} className="text-center py-12 text-slate-400">No tracking events for these filters.</td></tr>)}
              {items.map(r => (
                <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${ROW_TINT[r.statusId] ?? ''}`}>
                  <td className="px-4 py-3 align-top text-xs">
                    <div className="text-slate-600">Merchant: <span className="text-slate-900 font-medium">{r.order?.merchant?.companyName ?? '—'}</span></div>
                    <div className="text-slate-600">Order No: <span className="text-slate-900 font-mono">{r.order?.orderNo ?? '—'}</span></div>
                    {orderType === 'B2B' ? (
                      <div className="text-slate-600">LR No: <span className="font-mono text-slate-900">{r.lrNo ?? r.awbNo ?? '—'}</span>
                        {r.awbNo && r.awbNo !== r.lrNo && <span className="text-slate-400"> · Master AWB {r.awbNo}</span>}
                      </div>
                    ) : (
                      <div className="text-slate-600">AWB No: <span className="font-mono text-slate-900">{r.awbNo ?? r.lrNo ?? '—'}</span></div>
                    )}
                    {/* The delivery appointment booked against this LR. It belongs on the scan row
                        because the person reading a scan is the person who needs to know a slot
                        exists — and the number opens the Appointments module filtered to this LR.
                        An auto-raised row that still has no date says so rather than showing blank. */}
                    {r.appointment && (
                      <div className="text-slate-600">Appointment:{' '}
                        <a href={`/appointments?lrNo=${encodeURIComponent(r.lrNo ?? r.order?.lrNo ?? '')}`}
                          className="text-primary hover:underline font-mono">
                          {r.appointment.appointmentRef || `#${r.appointment.id}`}
                        </a>
                        {r.appointment.appointmentDate && (
                          <span className="text-slate-500"> · {new Date(r.appointment.appointmentDate).toLocaleDateString('en-IN')}
                            {r.appointment.appointmentTime ? ` ${r.appointment.appointmentTime}` : ''}</span>
                        )}
                        {r.appointment.pending && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">date pending</span>}
                        {r.appointment.poNumber && <span className="text-slate-500"> · PO {r.appointment.poNumber}</span>}
                      </div>
                    )}
                    <div className="text-slate-600">Customer Mobile: <span className="font-mono">{r.order?.customerMobile ?? '—'}</span></div>
                    <div className="text-slate-600 flex items-center gap-1">Courier: {r.courier?.name ? (
                      <span className="inline-flex items-center gap-1 text-slate-900 font-bold">
                        <Truck size={12} className="text-slate-800 shrink-0" />
                        {r.courier.name}
                      </span>
                    ) : <span className="text-slate-400">—</span>}</div>
                  </td>
                  <td className="px-4 py-3 align-top text-xs">
                    <div>Remarks: <span className="text-slate-900">{r.statusMessage ?? 'N/A'}</span></div>
                    <div>Created At: <span className="text-slate-900">{new Date(r.createdAt).toLocaleString('en-IN')}</span></div>
                    <div>Charged Weight: <span className="text-slate-900">{r.chargedWeight ?? 'N/A'}</span></div>
                    <div>Dimensions: <span className="text-slate-900">{(r.lengthCm || r.widthCm || r.heightCm) ? `${r.lengthCm ?? '—'} × ${r.widthCm ?? '—'} × ${r.heightCm ?? '—'} cm` : 'N/A'}</span></div>
                  </td>
                  <td className="px-4 py-3 align-top text-xs">
                    <div>User: <span className="text-slate-900">{userLabel(r.byUserId, r.byUserName)}</span></div>
                    <div>Event: <span className={`text-xs px-2 py-0.5 rounded ${STATUS_TONE[r.statusId] ?? 'bg-slate-100 text-slate-600'}`}>{statusMap[r.statusId] ?? r.statusId}</span></div>
                    <div>Status Code: <span className="font-mono text-slate-900">{r.statusId}</span></div>
                    {r.statusId === 9 && r.rawPayload?.ndrReason && (
                      <div className="mt-1 text-rose-700 font-medium bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded text-[11px] inline-block">NDR: {((ndrStatuses ?? []).find(n => n.id === Number(r.rawPayload.ndrReason))?.name) ?? `#${r.rawPayload.ndrReason}`}</div>
                    )}
                    {r.podFile && (
                      <SignedFileLink href={r.podFile} download downloadName={r.lrNo || r.awbNo || `pod-${r.id}`} className="inline-flex items-center gap-1 text-primary hover:underline mt-1">
                        <Paperclip size={11} /> View POD
                      </SignedFileLink>
                    )}
                    {r.receiverName && (
                      <div className="text-emerald-700 mt-0.5">Received by: {r.receiverName}{r.receiverPhone ? ` · ${r.receiverPhone}` : ''}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs">
                    <div>Pincode: <span className="text-slate-900">{r.scanPincode ?? 'N/A'}</span></div>
                    <div>City: <span className="text-slate-900">{r.scanCity ?? 'N/A'}</span></div>
                    <div>State: <span className="text-slate-900">{r.scanState ?? 'N/A'}</span></div>
                    <div className="font-mono">Lat: {r.scanLat ?? 'N/A'} / Lng: {r.scanLng ?? 'N/A'}</div>
                  </td>
                  <td className="px-4 py-3 align-top text-xs">{userLabel(r.assignedUserId, r.assignedUserName)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-600">
            <div>Page {page} of {Math.max(1, Math.ceil(total / pageSize))}</div>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50">Previous</button>
              <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}
                className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Widened from 2xl: the form carries the AWB list, a status picker, location fields and a
          remark side by side, and at 2xl the AWB textarea and the longer status names were being
          wrapped into unreadable columns. */}
      <Modal isOpen={showScan} onClose={() => setShowScan(false)} title="Scan Status" subtitle="Add a tracking event to one or more AWBs" maxWidth="5xl">
        <ScanDialog orderType={orderType} onClose={() => setShowScan(false)} onSuccess={() => { refetch(); }} />
      </Modal>
    </div>
  );
}

export function TrackingB2cPage() { return <TrackingPage orderType="B2C" />; }
export function TrackingB2bPage() { return <TrackingPage orderType="B2B" />; }

// Unified Tracking hub: internal B2C/B2B tab toggle, single nav item
export function TrackingHubPage() {
  const [ot, setOt] = useState<'B2C' | 'B2B'>('B2C');
  return (
    <div>
      <div className="px-6 pt-6 pb-2 max-w-[1400px] mx-auto">
        <div className="inline-flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
          <button onClick={() => setOt('B2C')}
            className={`px-4 py-1.5 text-sm rounded-md transition ${ot === 'B2C' ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            B2C
          </button>
          <button onClick={() => setOt('B2B')}
            className={`px-4 py-1.5 text-sm rounded-md transition ${ot === 'B2B' ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            B2B
          </button>
        </div>
      </div>
      <TrackingPage orderType={ot} />
    </div>
  );
}
